import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Plus, ReceiptText, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DianReadinessBanner } from "@/components/companies/DianReadinessBanner";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { RequireAuth } from "@/components/RequireAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { POS_STATUS_BADGE_VARIANT } from "@/components/pos-documents/format";
import { usePosAdjustments } from "@/lib/api/pos-document-adjustments.queries";
import { useActiveCompany } from "@/lib/company-context";

export const Route = createFileRoute("/companies/$companyId/pos/ajustes/")({
  component: PosAdjustmentsPage,
});

function PosAdjustmentsPage() {
  return (
    <RequireAuth>
      <AppShell>
        <PosAdjustmentsContent />
      </AppShell>
    </RequireAuth>
  );
}

function PosAdjustmentsContent() {
  const formatPosMoney = useMoneyFormatter();
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const navigate = useNavigate();
  const { setActiveCompanyId } = useActiveCompany();
  const list = usePosAdjustments(companyId, { pageSize: 50 });

  useEffect(() => {
    setActiveCompanyId(companyId);
  }, [companyId, setActiveCompanyId]);

  if (list.isError) {
    return (
      <ErrorState
        title={t("pos.ajuste.list.error_title")}
        description={t("pos.ajuste.list.error_body")}
      />
    );
  }

  return (
    <div className="space-y-6">
      <DianReadinessBanner companyId={companyId} documentKind="pos_ajuste" />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/companies/$companyId/pos" params={{ companyId }} className="text-sm text-muted-foreground">
            {t("pos.ajuste.list.back")}
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">{t("pos.ajuste.list.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("pos.ajuste.list.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => void list.refetch()} disabled={list.isFetching}>
            <RefreshCw className={list.isFetching ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
            {t("pos.list.refresh")}
          </Button>
          <Button onClick={() => void navigate({ to: "/companies/$companyId/pos/ajustes/new", params: { companyId } })}>
            <Plus className="h-4 w-4" />
            {t("pos.ajuste.list.new")}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          {list.isPending ? (
            <p className="text-sm text-muted-foreground">{t("pos.ajuste.list.loading")}</p>
          ) : (list.data?.adjustments.length ?? 0) === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <ReceiptText className="h-8 w-8 text-muted-foreground" />
              <p className="font-medium">{t("pos.ajuste.list.empty_title")}</p>
              <p className="text-sm text-muted-foreground">{t("pos.ajuste.list.empty_body")}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("pos.table.document")}</TableHead>
                  <TableHead>{t("pos.ajuste.table.original")}</TableHead>
                  <TableHead>{t("pos.ajuste.table.type")}</TableHead>
                  <TableHead>{t("pos.table.amount")}</TableHead>
                  <TableHead>{t("pos.table.status")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(list.data?.adjustments ?? []).map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer"
                    onClick={() => void navigate({
                      to: "/companies/$companyId/pos/ajustes/$adjustmentId",
                      params: { companyId, adjustmentId: row.id },
                    })}
                  >
                    <TableCell className="font-medium">{row.documentId}</TableCell>
                    <TableCell>{row.originalDocumentId}</TableCell>
                    <TableCell>{t(`pos.ajuste.note_type.${row.noteType}`)} · {row.adjustmentTypeCode}</TableCell>
                    <TableCell>{formatPosMoney(row.payableAmount)}</TableCell>
                    <TableCell>
                      <Badge variant={POS_STATUS_BADGE_VARIANT[row.status]}>
                        {t(`pos.status.${row.status}`)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
